import Add from "@mui/icons-material/Add";
import Forward from "@mui/icons-material/Forward";
import Replay from "@mui/icons-material/Replay";
import Undo from "@mui/icons-material/Undo";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { StatusChip } from "components/StatusChip";
import {
  ReceivedEmailActionKind,
  ReceivedEmailAttention,
  ReceivedEmailRejectionReason,
  ReceivedEmailStatus,
} from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import type { InboundEmailRow } from "./senderDialogValues";

import {
  attachmentsLabel,
  actionState,
  attentionDisplay,
  statusDisplay,
  receivedLabel,
  REJECTION_LABELS,
} from "./displayText";
import { InboundExpandCell } from "./InboundExpandCell";

/**
 * The rejection reason is folded into the Status chip rather than given a column of
 * its own, so there is no column for the filter popover to derive a chip from. It
 * matters enough to filter by — "show me everything we refused as not allowlisted" is
 * the question this listing exists to answer — so it is declared directly.
 */
export const INBOUND_ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  {
    // Whether resolution could name an organization at all. Declared separately
    // rather than as a null option on the Organization column's tenant picker:
    // that picker is shared by 25 grids and serializes tenant ids, with no way to
    // say "none".
    key: "attributed",
    header: "Attributed",
    filterKeyType: "boolean",
  },
  {
    key: "rejectionReason",
    header: "Rejection Reason",
    filterKeyType: "enum",
    enumValues: Object.values(ReceivedEmailRejectionReason),
    // The popover hands us a plain string, so read the label map by key rather than
    // routing back through statusDisplay, which wants a whole row.
    enumPresentationFunction: (value) =>
      Object.entries(REJECTION_LABELS).find(([reason]) => reason === value)?.[1] ?? value,
  },
];

/**
 * How each status reads in the filter popover. The chip on a row says what happened
 * to *that* email ("Partial · 2 failed"); these name the set being selected.
 */
export const STATUS_FILTER_LABELS: Record<string, string> = {
  [ReceivedEmailStatus.Filed]: "Filed",
  [ReceivedEmailStatus.Partial]: "Partially filed",
  [ReceivedEmailStatus.NothingFiled]: "Nothing filed",
  [ReceivedEmailStatus.NoAttachments]: "No attachments",
  [ReceivedEmailStatus.FailedValidation]: "Failed validation",
  [ReceivedEmailStatus.HeldEncampSender]: "Held — Encamp sender",
  [ReceivedEmailStatus.HeldAmbiguous]: "Held — ambiguous sender",
  [ReceivedEmailStatus.Errored]: "Errored",
  [ReceivedEmailStatus.Processing]: "Processing",
};

/** How each attention value reads in the filter popover. */
export const ATTENTION_FILTER_LABELS: Record<string, string> = {
  [ReceivedEmailAttention.Needed]: "Needs attention",
  [ReceivedEmailAttention.Dismissed]: "Dismissed",
  [ReceivedEmailAttention.None]: "Nothing to do",
};

/**
 * How far to indent the detail panel so its content starts where the row's own data
 * does, rather than flush against the grid's left edge.
 *
 * Measured rather than derived: 50px for the chevron column plus the 10px of cell
 * padding before a cell's text. `GRID_DETAIL_PANEL_TOGGLE_COL_DEF.width` says 40,
 * but the shared DataGrid renders that column at 50, so reading the constant lands
 * the panel 10px short of the Received column — which is exactly the misalignment
 * this is here to fix.
 */
export const DETAIL_PANEL_INDENT_PX = 60;

/** None carries a value worth filtering on: derived text, a button, a chevron. */
export const INBOUND_EXCLUDE_FILTER_COLUMNS = [
  "attachments",
  "actions",
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field,
  // The popover derives a filter from every column it is not told to skip, and these
  // two produced controls the resolver does not parse: setting them changed nothing
  // and the grid still returned every row. The search box already matches both
  // fields, so this removes a second, broken path to the same thing.
  "fromAddress",
  "subject",
];

/**
 * The per-column menu offers sort, filter, hide-column and manage-columns — all four
 * of which the toolbar above now owns, and headers already sort on click. Leaving it
 * on also cost a phantom scrollbar: with flex columns filling the width exactly, the
 * last header's menu button overhangs by its own 23px and the grid scrolls to show
 * nothing.
 */
const noMenu = (
  col: OmnisearchGridColDef<InboundEmailRow>
): OmnisearchGridColDef<InboundEmailRow> => ({ ...col, disableColumnMenu: true });

export function buildInboundColumns({
  onAddSender,
  onReplay,
  onRoute,
  onDismiss,
  busy,
}: {
  /** The row a replay or route was just requested for, and the status it had then. */
  busy?: { id: string; status: ReceivedEmailStatus } | null;
  onAddSender?: (fromAddress: string) => void;
  onReplay?: (row: InboundEmailRow) => void;
  onRoute?: (row: InboundEmailRow) => void;
  /** Mark a row as needing no action, or take that back. */
  onDismiss?: (row: InboundEmailRow, dismissed: boolean) => void;
} = {}): OmnisearchGridColDef<InboundEmailRow>[] {
  const base: OmnisearchGridColDef<InboundEmailRow>[] = [
    // The column spread carries the sizing and alignment; the cell is ours because
    // the default renderCell draws nothing through this wrapper — it reads grid state
    // the shared DataGrid does not thread to it. Driving `toggleDetailPanel` off
    // `apiRef` is what the other detail-panel grids here do for the same reason.
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      // Not the reader's to hide, and it has no header to name it by in the
      // Manage Columns list.
      hideable: false,
      renderCell: ({ id }) => <InboundExpandCell id={id} />,
    },
    {
      field: "receivedAt",
      headerName: "Received",
      flex: 1,
      minWidth: 130,
      // The app's standard range filter, rather than a bespoke window control beside
      // the heading — which sat outside the filter popover where every other filter
      // lives, and read as though it governed the whole page rather than this grid.
      filterKeyType: "date",
      valueGetter: (_v, row) => new Date(row.receivedAt),
      renderCell: ({ row }) => (
        <Typography variant="body2">{receivedLabel(row.receivedAt)}</Typography>
      ),
    },
    {
      field: "tenant",
      headerName: "Organization",
      flex: 1.2,
      minWidth: 140,
      // Serialized by MultipleTenantPicker as `tenantId:a|b`, which the resolver's
      // `tenantId` chip resolves. The picker cannot express "no organization" — the
      // separate `attributed` filter above is how that is asked for.
      filterKeyType: "multipleTenant",
      valueGetter: (_v, row) => row.tenant?.name ?? "",
      // Left empty when nothing matched, rather than labelled. An empty cell in a
      // column of names is the most obvious thing on the row, and the Status column
      // already says why — a word here only invited the question of what it meant.
      renderCell: ({ row }) =>
        row.tenant ? <TextCellWithTooltip>{row.tenant.name}</TextCellWithTooltip> : null,
    },
    {
      field: "fromAddress",
      headerName: "From",
      flex: 2,
      minWidth: 190,
      valueGetter: (_v, row) => row.fromAddress ?? "",
      // Single line on purpose. This used to carry "Not on any allowlist" underneath,
      // which the Status column already says — and a cell that is two lines on some
      // rows and one on others makes the grid change height as you filter, which is
      // what made the page jump around.
      renderCell: ({ row }) => <TextCellWithTooltip>{row.fromAddress ?? "—"}</TextCellWithTooltip>,
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 2,
      minWidth: 160,
      valueGetter: (_v, row) => row.subject || "(no subject)",
      // Clips mid-word without this: the shared grid makes cells flex containers, and
      // `text-overflow` does not apply to a flex item.
      renderCell: ({ row }) => (
        <TextCellWithTooltip>{row.subject || "(no subject)"}</TextCellWithTooltip>
      ),
    },
    {
      field: "attachments",
      headerName: "Attachments",
      flex: 1,
      minWidth: 120,
      sortable: false,
      valueGetter: (_v, row) => attachmentsLabel(row),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 170,
      filterKeyType: "enum",
      enumValues: Object.values(ReceivedEmailStatus),
      enumPresentationFunction: (value) => STATUS_FILTER_LABELS[value] ?? value,
      renderCell: ({ row }) => {
        const { color, label, help } = statusDisplay(row);
        // Always a chip. The old listing drew neutral statuses as plain text because
        // every row in it was a routine refusal, so the colour said nothing. In one
        // merged grid they are mixed and the colour is the fastest thing to scan.
        // maxWidth so a long label ellipsizes inside the cell rather than being
        // clipped by it, which is how the old sentence-long labels failed.
        const rendered = <StatusChip color={color} label={label} sx={{ maxWidth: "100%" }} />;
        // StatusChip is not forwardRef, so Tooltip needs an element to attach to. The
        // help text is the whole explanation of what happened, so it always stays.
        return help ? (
          <Tooltip title={help}>
            <span>{rendered}</span>
          </Tooltip>
        ) : (
          rendered
        );
      },
    },
    {
      // Its own column rather than folded into the status chip. The two say different
      // things — what happened, and whether anyone still owes it something — and a
      // dismissed row used to hide what happened to it inside a tooltip.
      field: "attention",
      headerName: "Attention",
      flex: 1,
      minWidth: 130,
      sortable: false,
      // Declared here rather than in the additional keys above, which are for filters
      // with no column of their own. One filter rather than two: needs attention and
      // dismissed are the same question asked twice, and with "nobody was ever asked"
      // they partition every row.
      filterKeyType: "enum",
      enumValues: Object.values(ReceivedEmailAttention),
      enumPresentationFunction: (value) => ATTENTION_FILTER_LABELS[value] ?? value,
      renderCell: ({ row }) => {
        const attention = attentionDisplay(row);
        if (!attention) return null;
        const chip = <StatusChip color={attention.color} label={attention.label} />;
        return attention.help ? (
          <Tooltip title={attention.help}>
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      },
    },
  ];

  if (!onAddSender && !onReplay && !onRoute && !onDismiss) return base.map(noMenu);

  /**
   * A row that was just acted on, until its status actually moves.
   *
   * The lambda sets PROCESSING, not the API, so for a second or two after pressing
   * Replay the row still reads the status it had and offers the same action again. A
   * second press is harmless — the claim discards the loser — but it costs an invoke
   * and reads as if the first press did nothing. Comparing the status rather than
   * timing it means the control comes back on its own the moment the row moves,
   * whatever it moves to.
   */
  const actedOn = (row: InboundEmailRow) => busy?.id === row.id && busy.status === row.status;

  const withAction: OmnisearchGridColDef<InboundEmailRow>[] = [
    ...base,
    {
      // The field must literally be `actions`: the shared grid's hover reveal is keyed
      // on `data-field='actions'`, so any other name leaves the column permanently
      // visible.
      field: "actions",
      type: "actions",
      headerName: "",
      // One overflow button rather than up to four bare icons, which read as a row of
      // controls to press rather than as something you go looking for. Matches the
      // senders grid beside it.
      width: 60,
      hideable: false,
      getActions: ({ row }) => {
        const actions = [];
        // Route first: for held mail, choosing the organization is
        // the decision, and it is the only control a held row offers.
        if (onRoute && actionState(row, ReceivedEmailActionKind.Route).available) {
          actions.push(
            <GridActionsCellItem
              key="route"
              showInMenu
              disabled={actedOn(row)}
              icon={<Forward fontSize="small" />}
              label="Route to an organization"
              onClick={() => onRoute(row)}
            />
          );
        }
        // Only where the API would accept it. The row already carries the verdict
        // computed once by the API, so the control appears exactly where pressing it works
        // rather than on every refusal and failing on most.
        if (onReplay && actionState(row, ReceivedEmailActionKind.Replay).available) {
          actions.push(
            <GridActionsCellItem
              key="replay"
              showInMenu
              disabled={actedOn(row)}
              icon={<Replay fontSize="small" />}
              label="Replay through intake"
              onClick={() => onReplay(row)}
            />
          );
        }
        // Only where allowlisting is the fix, which is narrower than "nobody claimed
        // it". An ambiguous sender is already claimed by two organizations, so adding
        // a third makes the mail *more* unroutable — that one is fixed by removing a
        // claim on the senders grid. A switched-off sender is fixed by turning its
        // existing entry back on, and an unreadable From header has no address to add.
        if (
          onAddSender &&
          row.fromAddress &&
          row.rejectionReason === ReceivedEmailRejectionReason.SenderNotAllowed
        ) {
          actions.push(
            <GridActionsCellItem
              key="add"
              showInMenu
              icon={<Add fontSize="small" />}
              // The address is in the row the menu was opened from, so the item does
              // not repeat it.
              label="Allowlist this sender"
              onClick={() => onAddSender(row.fromAddress ?? "")}
            />
          );
        }
        // Last, and offered on anything a person might still be asked about: the
        // third exit from the needs-attention view, for mail that should not be filed
        // anywhere. Reversible, so a dismissed row offers the way back.
        if (onDismiss && actionState(row, ReceivedEmailActionKind.Dismiss).available) {
          actions.push(
            <GridActionsCellItem
              key="dismiss"
              showInMenu
              icon={<VisibilityOff fontSize="small" />}
              label="Dismiss — needs no action"
              onClick={() => onDismiss(row, true)}
            />
          );
        }
        if (onDismiss && row.dismissedAt) {
          actions.push(
            <GridActionsCellItem
              key="restore"
              showInMenu
              icon={<Undo fontSize="small" />}
              label="Restore — take back the dismissal"
              onClick={() => onDismiss(row, false)}
            />
          );
        }
        return actions;
      },
    },
  ];
  return withAction.map(noMenu);
}
